import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, Send, X, Sparkles } from "lucide-react";
import { wonders } from "../data/wonders";
import { finalQuiz } from "../data/finalQuiz";

/**
 * YzaBot - a fully offline, rule-based chatbot that knows the entire
 * presentation. No API, no internet required. It matches user questions
 * against a knowledge base built from the wonders data.
 */

const PRESENTER = "Yzabelle";
const TEACHER = "Ms. Sutherland";

const SUGGESTED = [
  "Who is presenting?",
  "Tell me about the Great Wall",
  "Where is Petra?",
  "Who built Machu Picchu?",
  "How tall is Christ the Redeemer?",
  "What is the Taj Mahal made of?",
  "How many steps does Chichen Itza have?",
  "How many people fit in the Colosseum?",
  "What is the quiz about?",
];

const norm = (s) => s.toLowerCase().replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();

const findWonder = (q) => {
  const n = norm(q);
  const map = [
    { keys: ["great wall", "china", "wall of china"], idx: 0 },
    { keys: ["petra", "jordan", "rose city", "treasury"], idx: 1 },
    { keys: ["christ", "redeemer", "brazil", "rio", "jesus statue"], idx: 2 },
    { keys: ["machu", "picchu", "peru", "inca", "incas"], idx: 3 },
    { keys: ["chichen", "itza", "mexico", "maya", "mayan", "el castillo", "pyramid"], idx: 4 },
    { keys: ["colosseum", "coliseum", "rome", "italy", "roman"], idx: 5 },
    { keys: ["taj", "mahal", "india", "marble", "shah jahan"], idx: 6 },
  ];
  for (const m of map) {
    if (m.keys.some((k) => n.includes(k))) return wonders[m.idx];
  }
  return null;
};

const wonderAnswer = (w, q) => {
  const n = norm(q);
  if (/(where|country|location|continent)/.test(n))
    return `${w.name} is in ${w.country} ${w.flag}, on the continent of ${w.continent}.`;
  if (/(who built|who made|builder|made by|built by)/.test(n))
    return `${w.name} was built by ${w.builtBy}.`;
  if (/(how old|age|when built|how long ago|built in)/.test(n))
    return `${w.name} is ${w.age}.`;
  if (/(tall|height|big|size|how many|how long|how far|how wide)/.test(n))
    return `Fun fact: ${w.funFact}`;
  if (/(why|reason|purpose|for what)/.test(n))
    return `${w.summary} ${w.didYouKnow}`;
  if (/(fun fact|amazing|cool|wow|interesting)/.test(n))
    return `Fun fact about ${w.name}: ${w.funFact}`;
  if (/(did you know|secret|trivia)/.test(n))
    return `Did you know? ${w.didYouKnow}`;
  if (/(vocabulary|word|meaning|define)/.test(n))
    return `Vocabulary - ${w.vocabulary.word}: ${w.vocabulary.meaning}`;
  // default: short summary
  return `${w.emoji} ${w.name} (${w.country}, ${w.continent}). ${w.summary} Fun fact: ${w.funFact}`;
};

const answer = (raw) => {
  if (!raw || !raw.trim()) return "Ask me anything about the New 7 Wonders of the World!";
  const q = norm(raw);

  // greetings
  if (/^(hi|hello|hey|hola|kumusta|yo|sup)\b/.test(q))
    return `Hi there! I'm YzaBot. I know everything about ${PRESENTER}'s presentation. Try asking about any of the 7 wonders!`;
  if (/(your name|who are you|what are you)/.test(q))
    return `I'm YzaBot - ${PRESENTER}'s helper for this presentation! Ask me about any wonder, the quiz, or the slideshow.`;
  if (/(thank|thanks|salamat)/.test(q))
    return `You're welcome! Enjoy the show 🎉`;
  if (/(bye|goodbye|see you)/.test(q))
    return `Bye! Hope you learned something amazing today!`;

  // about the presenter / teacher / class
  if (/(presenter|presented by|who is presenting|who made this|whose presentation)/.test(q))
    return `This presentation is by ${PRESENTER} 👧 for ${TEACHER}'s Grade 3 class.`;
  if (/(teacher|ms\.? sutherland|sutherland)/.test(q))
    return `The teacher is ${TEACHER} 👩‍🏫.`;
  if (/(grade|class|what grade)/.test(q))
    return `This is a Grade 3 class presentation.`;
  if (/(yzabelle|yzabel|presenter name)/.test(q))
    return `${PRESENTER} is the presenter! She made this whole interactive show.`;
  if (/(developer|made by|coded|built the website|built the site|gianquila|gian|developed by)/.test(q))
    return `This website was developed by GianQuila ✨.`;

  // how the website works
  if (/(how many slides|number of slides|total slides)/.test(q))
    return `There are 10 slides total: 1 title, 7 wonders, 1 quiz, and 1 finale.`;
  if (/(how to (use|navigate|move)|navigate|next slide|previous slide|controls)/.test(q))
    return `Use the right arrow → to go next, left arrow ← to go back, or click the Next/Previous buttons. You can also swipe on a tablet!`;
  if (/(dark mode|theme)/.test(q))
    return `Tap the moon icon (top-right) to switch to dark mode.`;
  if (/(fullscreen|full screen)/.test(q))
    return `Tap the fullscreen icon (top-right) to make the presentation cover the whole screen.`;
  if (/(print|pdf|download)/.test(q))
    return `Tap the printer icon (top-right) to print or save as PDF.`;
  if (/(music|sound|audio)/.test(q))
    return `Tap the music icon (top-right) to play classroom-friendly background music.`;

  // about the quiz
  if (/(quiz|question|test|review)/.test(q)) {
    const qs = finalQuiz.map((q, i) => `${i + 1}. ${q.question}`).join(" | ");
    return `The quiz at slide 9 has ${finalQuiz.length} review questions: ${qs}. Pick the right answer for a "Correct!" message, or try again if it's wrong.`;
  }

  // count of wonders
  if (/(how many wonders|number of wonders|all wonders|list the wonders|seven wonders|7 wonders)/.test(q))
    return `The 7 wonders in this presentation are: ${wonders.map((w) => w.name).join(", ")}.`;

  // try matching a specific wonder
  const w = findWonder(raw);
  if (w) return wonderAnswer(w, raw);

  // help / fallback
  if (/(help|what can you (do|answer)|menu)/.test(q))
    return `I can tell you about each of the 7 wonders, the quiz, the presenter, and how to use the slideshow. Try: "Tell me about the Colosseum" or "Who built the Taj Mahal?"`;

  return `Hmm, I don't know that one. Try asking about one of the 7 wonders (Great Wall, Petra, Christ the Redeemer, Machu Picchu, Chichen Itza, Colosseum, Taj Mahal), the quiz, or the presenter Yzabelle.`;
};

const initialMessages = [
  {
    role: "bot",
    text: "Hi! I'm YzaBot 🤖 - Yzabelle's helper for this presentation. Ask me anything about the 7 wonders!",
  },
];

export const YzaBot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(initialMessages);
  const [input, setInput] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }, [messages, open]);

  const send = (text) => {
    const t = (text ?? input).trim();
    if (!t) return;
    const next = [...messages, { role: "user", text: t }];
    const reply = answer(t);
    next.push({ role: "bot", text: reply });
    setMessages(next);
    setInput("");
  };

  return (
    <>
      <button
        className="icon-btn"
        onClick={() => setOpen((o) => !o)}
        title="Chat with YzaBot"
        aria-label="Open YzaBot chatbot"
        data-testid="yzabot-toggle-btn"
        style={{ position: "relative" }}
      >
        <Bot className="w-5 h-5" />
        {!open && (
          <span
            style={{
              position: "absolute",
              top: -2,
              right: -2,
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "#10B981",
              border: "2px solid white",
            }}
          />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.18 }}
            className="yzabot-panel"
            data-testid="yzabot-panel"
          >
            <div className="yzabot-header">
              <div className="yzabot-avatar">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div style={{ flex: 1 }}>
                <div className="font-heading font-bold text-base text-slate-900">YzaBot</div>
                <div className="font-accent text-xs text-slate-500 flex items-center gap-1">
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#10B981", display: "inline-block" }} />
                  Online - knows everything about this show
                </div>
              </div>
              <button
                className="yzabot-close"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                data-testid="yzabot-close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="yzabot-body" data-testid="yzabot-messages">
              {messages.map((m, i) => (
                <div key={i} className={`yzabot-msg yzabot-msg-${m.role}`}>
                  {m.role === "bot" && (
                    <div className="yzabot-msg-avatar">
                      <Bot className="w-3.5 h-3.5 text-white" />
                    </div>
                  )}
                  <div className="yzabot-bubble">{m.text}</div>
                </div>
              ))}
              <div ref={endRef} />
            </div>

            {messages.length <= 2 && (
              <div className="yzabot-suggestions">
                <div className="font-accent text-xs text-slate-500 font-semibold mb-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Try asking:
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {SUGGESTED.map((s, i) => (
                    <button
                      key={i}
                      className="yzabot-chip"
                      onClick={() => send(s)}
                      data-testid={`yzabot-suggestion-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <form
              className="yzabot-footer"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask me anything..."
                className="yzabot-input"
                data-testid="yzabot-input"
                aria-label="Ask YzaBot"
              />
              <button
                type="submit"
                className="yzabot-send"
                aria-label="Send"
                data-testid="yzabot-send-btn"
                disabled={!input.trim()}
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
